'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from '../modal';
import { createApiClient, buildBackendUrl, formatDateToLocal } from '@textile-erp/core';
import { ChevronRight, Loader2, ClipboardList } from 'lucide-react';
import { ActionBadge as AuditActionBadge } from './audit-log-detail';
import { AuditLogEntry } from './audit-log-entry';
const monitoringClient = createApiClient(buildBackendUrl('/api/monitoring'));
function setToken() {
    const token = localStorage.getItem('textile_erp_token');
    if (token)
        monitoringClient.setAuthToken(token);
}
function LogEntry({ log }) {
    return _jsx(AuditLogEntry, { log: log });
}
function DateCard({ dateLabel, logs }) {
    const [expanded, setExpanded] = useState(true);
    return (_jsxs("div", { className: "rounded-xl border overflow-hidden shadow-sm border-gray-200 bg-gray-50 dark:bg-gray-900 dark:border-gray-700", children: [_jsxs("button", { onClick: () => setExpanded(p => !p), className: "w-full flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-800 hover:brightness-95 transition-colors text-left gap-3", children: [_jsxs("div", { className: "flex items-center gap-3 min-w-0", children: [_jsx("div", { className: "w-6 h-6 rounded-full bg-white/60 dark:bg-black/20 flex items-center justify-center flex-shrink-0 shadow-sm", children: _jsx(ChevronRight, { className: `w-3.5 h-3.5 text-gray-700 dark:text-gray-200 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}` }) }), _jsx("span", { className: "font-bold text-sm text-gray-800 dark:text-gray-100", children: dateLabel })] }), _jsx("div", { className: "flex items-center gap-2 flex-shrink-0", children: [...new Set(logs.map(l => l.action))].map(a => (_jsx(AuditActionBadge, { action: a }, a))) })] }), expanded && (_jsx("div", { className: "p-3 space-y-2 bg-white/50 dark:bg-black/10", children: logs.map(log => (_jsx(LogEntry, { log: log }, log.id))) }))] }));
}
export function RecordAuditLogsModal({ isOpen, onClose, title, apiPath, emptyLabel }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchLogs = useCallback(async (path) => {
        setLoading(true);
        setError(null);
        try {
            setToken();
            const res = await monitoringClient.get(path);
            setLogs(res.data ?? []);
        }
        catch (err) {
            setLogs([]);
            setError(err?.message || 'Failed to load audit logs');
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        if (isOpen && apiPath) {
            fetchLogs(apiPath);
        }
        else {
            setLogs([]);
        }
    }, [isOpen, apiPath, fetchLogs]);
    const groupedByDate = useMemo(() => {
        const map = new Map();
        for (const log of logs) {
            const dateKey = formatDateToLocal(new Date(log.createdAt));
            if (!map.has(dateKey))
                map.set(dateKey, []);
            map.get(dateKey).push(log);
        }
        const sortedKeys = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));
        return sortedKeys.map(key => ({
            dateKey: key,
            displayDate: new Date(key + 'T12:00:00').toLocaleDateString('en-IN', {
                weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
            }),
            logs: map.get(key),
        }));
    }, [logs]);
    return (_jsx(Modal, { isOpen: isOpen, onClose: onClose, title: title, size: "2xl", children: _jsx("div", { className: "min-h-[560px]", children: loading ? (_jsxs("div", { className: "flex items-center justify-center py-16 gap-2", children: [_jsx(Loader2, { className: "w-5 h-5 animate-spin text-gray-400" }), _jsx("span", { className: "text-sm text-gray-500", children: "Loading audit logs\u2026" })] })) : error ? (_jsxs("div", { className: "flex flex-col items-center justify-center py-16 gap-3 text-red-400", children: [_jsx(ClipboardList, { className: "w-10 h-10 opacity-30" }), _jsx("span", { className: "text-sm font-medium", children: "Error loading logs" }), _jsx("span", { className: "text-xs text-red-400", children: error })] })) : groupedByDate.length === 0 ? (_jsxs("div", { className: "flex flex-col items-center justify-center py-16 gap-3 text-gray-400", children: [_jsx(ClipboardList, { className: "w-10 h-10 opacity-30" }), _jsx("span", { className: "text-sm", children: emptyLabel ?? 'No audit logs found.' })] })) : (_jsx("div", { className: "space-y-3", children: groupedByDate.map(group => (_jsx(DateCard, { dateLabel: group.displayDate, logs: group.logs }, group.dateKey))) })) }) }));
}
